
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import FeatureGuidesSidebar from '@/components/knowledge-base/FeatureGuidesSidebar';
import GuideDetail from '@/components/knowledge-base/GuideDetail';
import { FeatureGuide } from '@/types/knowledgeBase';
import { getMasterLicenseFeatureGuides } from '@/data/masterLicenseFeatureGuides';
import { hasAccess } from '@/utils/permissionService';
import { useMasterLicense } from '@/hooks/useMasterLicense';
import KnowledgeBaseHeader from '@/components/knowledge-base/KnowledgeBaseHeader';

const MasterLicenseKnowledgeBasePage: React.FC = () => {
  console.log("Rendering MasterLicenseKnowledgeBasePage");
  const navigate = useNavigate();
  const { isLoading, handleLogout, user } = useMasterLicense();
  const [featureGuides, setFeatureGuides] = useState<FeatureGuide[]>([]);
  const [filteredGuides, setFilteredGuides] = useState<FeatureGuide[]>([]);
  const [selectedGuide, setSelectedGuide] = useState<FeatureGuide | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // Mock data if getMasterLicenseFeatureGuides is not available
  const getMockGuides = () => {
    return [
      {
        id: '1',
        title: 'Getting Started with the Master License Portal',
        description: 'Learn the basics of the Master License Portal',
        content: '# Getting Started\n\nWelcome to the Master License Portal. This guide will help you get started with the basic features.',
        category: 'Basics',
        lastUpdated: '2025-04-22',
        isNew: true
      },
      {
        id: '2',
        title: 'Managing License Keys',
        description: 'How to create and manage license keys',
        content: '# Managing License Keys\n\nThis guide explains how to create, assign, and revoke license keys.',
        category: 'Licenses',
        lastUpdated: '2025-04-18'
      },
      {
        id: '3',
        title: 'Domain Management',
        description: 'Learn how to manage domains for your licenses',
        content: '# Domain Management\n\nThis guide covers domain verification, adding domains to licenses, and troubleshooting domain issues.',
        category: 'Domains',
        lastUpdated: '2025-04-15'
      }
    ];
  };

  useEffect(() => {
    console.log("Fetching feature guides for Master License");
    
    try {
      // Try to get guides from the actual function
      const guides = getMasterLicenseFeatureGuides();
      
      // If no guides or the function doesn't exist, use mock data
      if (!guides || guides.length === 0) {
        console.log("No guides found, using mock data");
        const mockGuides = getMockGuides();
        setFeatureGuides(mockGuides);
        setFilteredGuides(mockGuides);
        setSelectedGuide(mockGuides[0]);
      } else {
        console.log("Guides loaded:", guides.length);
        setFeatureGuides(guides);
        setFilteredGuides(guides);
        if (guides.length > 0) {
          setSelectedGuide(guides[0]);
        }
      }
    } catch (error) {
      console.error("Error loading guides:", error);
      // Use mock data as fallback
      const mockGuides = getMockGuides();
      setFeatureGuides(mockGuides);
      setFilteredGuides(mockGuides);
      setSelectedGuide(mockGuides[0]);
    }
  }, []);

  useEffect(() => {
    if (featureGuides.length > 0) {
      let filtered = [...featureGuides];
      
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(
          guide => 
            guide.title.toLowerCase().includes(term) || 
            guide.description.toLowerCase().includes(term) ||
            guide.content.toLowerCase().includes(term)
        );
      }
      
      if (activeTab !== 'all') {
        filtered = filtered.filter(guide => guide.category.toLowerCase() === activeTab.toLowerCase());
      }
      
      setFilteredGuides(filtered);
      
      if (filtered.length > 0 && selectedGuide && !filtered.some(guide => guide.id === selectedGuide.id)) {
        setSelectedGuide(filtered[0]);
      }
    }
  }, [searchTerm, activeTab, featureGuides, selectedGuide]);
  
  const categories = ['all', ...new Set(featureGuides.map(guide => guide.category.toLowerCase()))];

  if (isLoading) {
    return (
      <MasterLicenseLayout
        isLoading={true}
        onLogout={handleLogout}
        activeTab="knowledge-base"
      >
        <div>Loading...</div>
      </MasterLicenseLayout>
    );
  }

  console.log("Rendering knowledge base with categories:", categories);
  console.log("Active tab:", activeTab);
  console.log("Filtered guides count:", filteredGuides.length);

  return (
    <MasterLicenseLayout
      isLoading={false}
      onLogout={handleLogout}
      activeTab="knowledge-base"
    >
      <div className="container mx-auto px-4 py-6">
        <KnowledgeBaseHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <FeatureGuidesSidebar
              filteredGuides={filteredGuides}
              selectedGuide={selectedGuide}
              setSelectedGuide={setSelectedGuide}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              categories={categories}
            />
          </div>
          
          <div className="md:col-span-2">
            <GuideDetail 
              selectedGuide={selectedGuide}
              allGuides={featureGuides}
              onSelectGuide={setSelectedGuide}
            />
          </div>
        </div>
      </div>
    </MasterLicenseLayout>
  );
};

export default MasterLicenseKnowledgeBasePage;
